import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ModalWithText from "./Modal";
import { TextStackModal } from "./TextStackModal";

const PAGE_SIZE = 30;

const themes = {
  light: {
    background: "#ffffff",
    color: "#000000",
    buttonBackground: "#f3f4f6",
    buttonText: "#000000",
    buttonHighlighted: "#2563eb",
    headerBackground: "#f9fafb",
    border: "#ccc",
  },
  dark: {
    background: "#121212",
    color: "#ffffff",
    buttonBackground: "#333",
    buttonText: "#ffffff",
    buttonHighlighted: "#2563eb",
    headerBackground: "#222",
    border: "#444",
  },
  kristak_pink: {
    background: "#ffe0f0",
    color: "#000000",
    buttonBackground: "#f3c1d1",
    buttonText: "#000000",
    buttonHighlighted: "#ff80a1",
    headerBackground: "#ff80a1",
    border: "#f89ec9",
  },
  blooper_blue: {
    background: "#e0f7ff",
    color: "#000000",
    buttonBackground: "#a0d9e8",
    buttonText: "#000000",
    buttonHighlighted: "#70a1ff",
    headerBackground: "#70a1ff",
    border: "#68a3d9",
  },
  glooper_green: {
    background: "#e0ffe0",
    color: "#000000",
    buttonBackground: "#a0e8a0",
    buttonText: "#000000",
    buttonHighlighted: "#70e070",
    headerBackground: "#70e070",
    border: "#68d968",
  },
  mlooper_mlurple: {
    background: "#f1e0ff",
    color: "#000000",
    buttonBackground: "#d4a0f0",
    buttonText: "#000000",
    buttonHighlighted: "#a070f0",
    headerBackground: "#a070f0",
    border: "#d1a3f4",
  },
  slopdog_maroon: {
    background: "#b66161ff",
    color: "#ffffff",
    buttonBackground: "#d37979ff",
    buttonText: "#ffffff",
    buttonHighlighted: "#e07070",
    headerBackground: "#a24f4f",
    border: "#6b2626ff",
  },
};

export default function CharacterMaker() {
  const [manifest, setManifest] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [stack, setStack] = useState([]);
  const [search, setSearch] = useState("");
  const [archives, setArchives] = useState({});
  const [archiveName, setArchiveName] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [textModalOpen, setTextModalOpen] = useState(false);

  const loadFromText = (newStack) => {
    setStack(newStack);
  };

  useEffect(() => {
    document.body.style.backgroundColor = themes[theme].background;
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const saved = localStorage.getItem("archives");
    if (saved) {
      setArchives(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = themes[theme].background;
  }, [theme]);

  const saveArchive = () => {
    if (!archiveName.trim()) {
      alert("Name your outfit to save it!");
      return;
    }

    const newArchives = {
      ...archives,
      [archiveName.trim()]: stack,
    };

    setArchives(newArchives);
    localStorage.setItem("archives", JSON.stringify(newArchives));
    setArchiveName("");
    alert(`"${archiveName}" saved!`);
  };

  const loadArchive = (name) => {
    const outfit = archives[name];
    if (!outfit) return;

    const migrated = outfit.map((item) => {
      if (typeof item.id === "number") {
        return {
          ...item,
          id: crypto.randomUUID(),
        };
      }

      if (!item.id) {
        return {
          ...item,
          id: crypto.randomUUID(),
        };
      }

      return item;
    });

    setStack(migrated);
  };

  const deleteArchive = (name) => {
    const newArchives = { ...archives };
    delete newArchives[name];
    setArchives(newArchives);
    localStorage.setItem("archives", JSON.stringify(newArchives));
  };

  useEffect(() => {
    fetch("/assets/manifest.json")
      .then((res) => {
        if (!res.ok)
          throw new Error("Failed to fetch manifest.json: " + res.status);
        return res.json();
      })
      .then((data) => {
        setManifest(data || {});
        const keys = Object.keys(data || {});
        if (keys.length) setActiveCategory(keys[0]);
      })
      .catch((err) => {
        console.error("Error loading manifest.json:", err);
        setManifest({});
      });
  }, []);

  const files = manifest[activeCategory ?? ""] || [];

  const filteredFiles = files.filter((src) => {
    const fileName = src.split("/").pop()?.toLowerCase() || "";
    return fileName.includes(search.replace(/\s+/g, "").toLowerCase());
  });

  const paginated = filteredFiles.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE
  );

  const addToStack = (src, category) => {
    setStack((prev) => [
      {
        id: crypto.randomUUID(),
        src,
        category,
      },
      ...prev,
    ]);
  };

  const removeFromStack = (id) => {
    setStack((prev) => prev.filter((it) => it.id !== id));
  };

  useEffect(() => {
    setPage(0);
  }, [activeCategory, search]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(stack);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setStack(reordered);
  };

  const downloadimg = async () => {
    if (stack.length === 0) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 200;
    canvas.height = 200;

    for (let i = stack.length - 1; i >= 0; i--) {
      const img = new Image();
      img.src = stack[i].src;
      await new Promise((resolve) => {
        img.onload = () => {
          ctx.drawImage(img, 0, 0, 200, 200);
          resolve();
        };
        img.onerror = resolve;
      });
    }

    const dataUrl = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.download = "buddy.png";
    link.href = dataUrl;
    link.click();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: themes[theme].background,
        color: themes[theme].color,
      }}
    >
      <div
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          gap: "30px",
          margin: " 0 20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2>HA Buddy Maker</h2>
          <img
            style={{
              width: "50px",
              height: "25px",
            }}
            alt="goat run gif"
            src="assets/trot.gif"
          />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ModalWithText />
          {theme === "slopdog_maroon" && <p>yes this looks awful on purpose</p>}
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: `1px solid ${themes[theme].border}`,
              backgroundColor: themes[theme].buttonBackground,
              color: themes[theme].buttonText,
            }}
          >
            {Object.keys(themes).map((themeName) => (
              <option key={themeName} value={themeName}>
                {themeName.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 24,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            backgroundColor: themes[theme].headerBackground,
            width: "500px",
            height: "100%",
          }}
        ></div>
        <div
          style={{
            paddingTop: "20px",
          }}
        >
          <div
            style={{
              width: 360,
              height: 480,
              position: "relative",
              border: `1px solid ${themes[theme].border}`,
              background: "white",
            }}
          >
            {[...stack].reverse().map((item, idx) => (
              <img
                key={item.id}
                src={item.src}
                alt={item.src}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  zIndex: idx + 1,
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button
              onClick={() => setStack([])}
              style={{
                backgroundColor: themes[theme].buttonBackground,
                color: themes[theme].buttonText,
                border: "none",
                borderRadius: "10px",
                padding: "10px",
              }}
            >
              Reset
            </button>
            <button
              onClick={downloadimg}
              style={{
                backgroundColor: themes[theme].buttonBackground,
                color: themes[theme].buttonText,
                border: "none",
                borderRadius: "10px",
                padding: "10px",
              }}
            >
              Download current HA buddy
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 6,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              type="text"
              placeholder="Outfit name..."
              value={archiveName}
              onChange={(e) => setArchiveName(e.target.value)}
              style={{
                flex: 1,
                padding: "6px 10px",
                border: `1px solid ${themes[theme].border}`,
                borderRadius: 6,
                backgroundColor: themes[theme].buttonBackground,
                color: themes[theme].color,
              }}
            />
            <button
              onClick={saveArchive}
              style={{
                backgroundColor: themes[theme].buttonBackground,
                color: themes[theme].buttonText,
                padding: "10px",
                border: "none",
                borderRadius: "10px",
              }}
            >
              Archive
            </button>
          </div>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="layers">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    marginTop: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    width: 350,
                  }}
                >
                  {stack.map((m, index) => {
                    const fileName = m.src
                      .split("/")
                      .pop()
                      ?.replace(/\.[^/.]+$/, "");
                    return (
                      <Draggable
                        key={m.id}
                        draggableId={String(m.id)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 6,
                              display: "flex",
                              justifyContent: "space-between",
                              border: `1px solid ${themes[theme].border}`,
                              background: snapshot.isDragging
                                ? "#e0e7ff"
                                : themes[theme].headerBackground,
                              cursor: "grab",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <img
                              src={m.src}
                              style={{ width: "50px" }}
                              alt={fileName}
                            />
                            <div>
                              {fileName}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromStack(m.id);
                                }}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "#ef4444",
                                  fontWeight: "bold",
                                  fontSize: 16,
                                  cursor: "pointer",
                                }}
                                title="Remove"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div
          style={{
            paddingTop: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            {Object.keys(manifest).length === 0 ? (
              <div>Loading</div>
            ) : (
              Object.keys(manifest).map((cat) => (
                <button
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border:
                      activeCategory === cat
                        ? `2px solid ${themes[theme].color}`
                        : `1px solid ${themes[theme].border}`,
                    background:
                      activeCategory === cat
                        ? themes[theme].buttonHighlighted
                        : themes[theme].buttonBackground,
                    color:
                      activeCategory === cat
                        ? "white"
                        : themes[theme].buttonText,
                  }}
                >
                  {cat}
                </button>
              ))
            )}
            <button
              onClick={() => setActiveCategory("saved")}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border:
                  activeCategory === "saved"
                    ? `2px solid ${themes[theme].color}`
                    : `1px solid ${themes[theme].border}`,
                background:
                  activeCategory === "saved"
                    ? themes[theme].buttonHighlighted
                    : themes[theme].buttonBackground,
                color:
                  activeCategory === "saved"
                    ? "white"
                    : themes[theme].buttonText,
              }}
            >
              outfit archive
            </button>
          </div>
          <div style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Search this category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                borderRadius: 6,
                border: `1px solid ${themes[theme].border}`,
                backgroundColor: themes[theme].buttonBackground,
                color: themes[theme].color,
              }}
            />
          </div>
          {activeCategory === "saved" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button onClick={() => setTextModalOpen(true)}>
                Load from text
              </button>
            </div>
          )}
          <TextStackModal
            isOpen={textModalOpen}
            onClose={() => setTextModalOpen(false)}
            stack={stack}
            onLoad={loadFromText}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              width: "800px",
              gap: 8,
              alignItems: "center",
            }}
          >
            {paginated.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  background: "white",
                  border: `1px solid ${themes[theme].border}`,
                  borderRadius: 6,
                  cursor: "pointer",
                }}
                onClick={() => addToStack(src, activeCategory)}
              />
            ))}
            {activeCategory === "saved" &&
              Object.keys(archives).map((name) => (
                <div
                  key={name}
                  onClick={() => loadArchive(name)}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    height: "100px",
                    width: "100px",
                    marginBottom: 4,
                    background: themes[theme].headerBackground,
                    padding: "4px 8px",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  {[...archives[name]].reverse().map((item, idx) => (
                    <img
                      key={item.id}
                      src={item.src}
                      alt=""
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100px",
                        height: "100px",
                        objectFit: "contain",
                        zIndex: idx + 1,
                        pointerEvents: "none",
                      }}
                    />
                  ))}

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteArchive(name);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      position: "absolute",
                      top: 2,
                      left: 2,
                      color: "#ef4444",
                      fontWeight: "bold",
                      cursor: "pointer",
                      zIndex: archives[name].length + 10,
                    }}
                    title="Delete this outfit"
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
            <div>
              Page {page + 1} of{" "}
              {Math.max(1, Math.ceil(filteredFiles.length / PAGE_SIZE))}
            </div>
            <button
              disabled={(page + 1) * PAGE_SIZE >= filteredFiles.length}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
            <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
              {activeCategory === "saved"
                ? Object.keys(archives).length
                : filteredFiles.length}{" "}
              items
            </div>
          </div>
          <h3>
            Drag and drop the item names to adjust layers, use X to remove them
            <br />
            <br />
            All credits for these assets go to Goatlings' site artists; <br />
            r00p, AiMcSweets, Bumblebunny, Ikkyoutan, Lotsysaur, Snow,
            sugarbear, Krisgoat, <br />
            as well as community members for the designs
            <br />
            (If I forgot to list an artist's name and/or if I put an item in the
            wrong category, or any other questions dm me on tumblr
            @enigmaeveryman)
          </h3>
        </div>

        <div
          style={{
            backgroundColor: themes[theme].headerBackground,
            width: "500px",
            height: "100%",
          }}
        ></div>
      </div>
      <style>
        {`
    @media (max-width: 768px) {
      .header-section {
        margin-left: 50px;
        margin-right: 150px;
      }
    }
  `}
      </style>
    </div>
  );
}
